import { Injectable, InjectionToken, Injector, Type } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { once } from 'lodash-es';
import { Observable, from, isObservable, noop, of } from 'rxjs';

import { FeatureToggleService, FeatureToggleType } from 'ish-core/feature-toggle.module';
import { applyConfiguration, getIdentityProvider } from 'ish-core/store/core/configuration';
import { whenTruthy } from 'ish-core/utils/operators';

import { IdentityProvider, TriggerReturnType } from './identity-provider.interface';

interface IdentityProviderImplementor {
  type: string;
  implementor: Type<IdentityProvider<unknown>>;
  feature?: FeatureToggleType;
}

export const IDENTITY_PROVIDER_IMPLEMENTOR = new InjectionToken<IdentityProviderImplementor>(
  'identityProviderImplementor'
);

@Injectable({ providedIn: 'root' })
export class IdentityProviderFactory {
  private instance: IdentityProvider<unknown>;
  private config: {
    [key: string]: unknown;
    type?: string;
  };
  private route: ActivatedRouteSnapshot;
  private state: RouterStateSnapshot;
  constructor(private store: Store, private injector: Injector, private featureToggleService: FeatureToggleService) {
    if (!SSR) {
      const activeIDP = localStorage.getItem('activeIdentityProvider');
      if (activeIDP) {
        this.store.dispatch(applyConfiguration({ identityProvider: activeIDP }));
      }
      this.store.pipe(select(getIdentityProvider), whenTruthy()).subscribe(config => {
        const provider = this.injector
          .get<IdentityProviderImplementor[]>(IDENTITY_PROVIDER_IMPLEMENTOR, [])
          .filter(p => (p.feature ? this.featureToggleService.enabled(p.feature) : true))
          .find(p => p.type === config?.type);

        if (!provider) {
          console.error('did not find identity provider for config', config);
        } else {
          const instance = this.injector.get(provider.implementor);

          /*
            If the code is called synchronously, the init method can call other actions that can trigger http requests.
            If an HTTP request is triggered before the identity provider constructor is finished, a "Null Pointer Exception" will be thrown in the identity-provider.interceptor.
            A delay with setTimeout is added to mitigate this problem
          */
          // TODO: Find a way to implement this without setTimeout()
          setTimeout(() => {
            instance.init(config);
          }, 1);

          this.instance = instance;
          this.config = config;
        }
      });
    } else {
      this.instance = {
        init: noop,
        intercept: (req, next) => next.handle(req),
        triggerLogin: () => true,
        triggerLogout: () => true,
        triggerInvite: () => true,
        getCapabilities: () => ({}),
      };
    }
  }

  private logNoIdpError = once(() =>
    console.error('No identity provider instance exists. Please double-check your configuration:', this.config)
  );

  getInstance() {
    if (!this.instance) {
      this.logNoIdpError();
    }
    return this.instance;
  }

  getType(): string {
    return this.config?.type;
  }
  handleSsoTriggerLogin(): void {
    const identityProvider = this.getInstance();
    const result = identityProvider.triggerLogin(this.route, this.state);
    this.toObservable(result).subscribe({
      next: () => console.debug('SSO login successful'),
      error: () => console.error('SSO login failed'),
    });
  }

  private toObservable(result: TriggerReturnType): Observable<boolean | UrlTree> {
    if (isObservable(result)) {
      return result;
    }

    return from(result instanceof Promise ? result : of(result));
  }
}
